import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Table, PencilSimple, Trash, ArrowSquareOut, Check, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api from "../lib/api";
import { formatCaptureDate } from "../lib/formatDate";

// Full CRUD manager for the user's bookmarked DingTalk sheet links (a
// simple manually-maintained list, not a live DingTalk API connection -
// see PRD 2026-07-31). Opened either from the compact sidebar card's "查看
// 全部表格" link or its per-row edit button.
export function DingtalkLinksDialog({ open, onOpenChange, links, onChange }) {
  const { t, i18n } = useTranslation();
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newUrl.trim()) {
      toast.error(t("dingtalkDialog.validationError"));
      return;
    }
    try {
      const { data } = await api.post("/dingtalk-links", { name: newName.trim(), url: newUrl.trim() });
      onChange([...links, data]);
      setNewName("");
      setNewUrl("");
    } catch (e) {
      toast.error(t("dingtalkDialog.createFailedError"));
    }
  };

  const startEdit = (link) => {
    setEditingId(link.id);
    setEditName(link.name);
    setEditUrl(link.url);
  };

  const saveEdit = async () => {
    if (!editName.trim() || !editUrl.trim()) {
      toast.error(t("dingtalkDialog.validationError"));
      return;
    }
    try {
      const { data } = await api.patch(`/dingtalk-links/${editingId}`, { name: editName.trim(), url: editUrl.trim() });
      onChange(links.map((l) => (l.id === editingId ? data : l)));
      setEditingId(null);
    } catch (e) {
      toast.error(t("dingtalkDialog.updateFailedError"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/dingtalk-links/${id}`);
      onChange(links.filter((l) => l.id !== id));
    } catch (e) {
      toast.error(t("dingtalkDialog.deleteFailedError"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dingtalk-links-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Table size={18} /> {t("dingtalkDialog.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleAdd} className="flex flex-col gap-2 border border-border bg-secondary/40 p-3">
          <Input data-testid="dingtalk-new-name-input" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder={t("dingtalkDialog.namePlaceholder")} className="rounded-none" />
          <Input data-testid="dingtalk-new-url-input" value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
            placeholder={t("dingtalkDialog.urlPlaceholder")} className="rounded-none" />
          <Button type="submit" data-testid="dingtalk-add-submit-button" className="rounded-none self-end">
            {t("dingtalkDialog.addButton")}
          </Button>
        </form>

        <div className="max-h-80 overflow-y-auto space-y-2" data-testid="dingtalk-links-manage-list">
          {links.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="dingtalk-links-manage-empty">
              {t("dingtalkDialog.emptyState")}
            </p>
          )}
          {links.map((link) => (
            <div key={link.id} className="border border-border p-2.5" data-testid={`dingtalk-manage-row-${link.id}`}>
              {editingId === link.id ? (
                <div className="flex flex-col gap-2">
                  <Input data-testid={`dingtalk-edit-name-input-${link.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-none" />
                  <Input data-testid={`dingtalk-edit-url-input-${link.id}`} value={editUrl} onChange={(e) => setEditUrl(e.target.value)} className="rounded-none" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" data-testid={`dingtalk-edit-cancel-${link.id}`} onClick={() => setEditingId(null)} className="rounded-none">
                      <X size={14} /> {t("dingtalkDialog.cancelButton")}
                    </Button>
                    <Button size="sm" data-testid={`dingtalk-edit-save-${link.id}`} onClick={saveEdit} className="rounded-none">
                      <Check size={14} /> {t("dingtalkDialog.saveButton")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center border border-green-600/30 bg-green-600/10 text-green-600 shrink-0">
                    <Table size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate">{link.name}</p>
                    <p className="text-xs text-muted-foreground">{t("taskSidebar.dingtalkUpdatedAt")} {formatCaptureDate(link.updated_at, i18n.language)}</p>
                  </div>
                  <button data-testid={`dingtalk-open-${link.id}`} onClick={() => window.open(link.url, "_blank", "noopener")} className="p-1.5 text-muted-foreground hover:text-primary shrink-0" title={t("taskSidebar.openDingtalkLink")}>
                    <ArrowSquareOut size={16} />
                  </button>
                  <button data-testid={`dingtalk-edit-${link.id}`} onClick={() => startEdit(link)} className="p-1.5 text-muted-foreground hover:text-primary shrink-0" title={t("taskSidebar.editDingtalkLink")}>
                    <PencilSimple size={16} />
                  </button>
                  <button data-testid={`dingtalk-delete-${link.id}`} onClick={() => handleDelete(link.id)} className="p-1.5 text-muted-foreground hover:text-destructive shrink-0">
                    <Trash size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
